/**
 * @file `LabShell` — single layout component the router renders for
 *       every lab route. Mirrors `SiteLayout` (www) and `AdminShell`
 *       (admin): chrome lives here, routed body renders into the
 *       `<Outlet />`.
 * @module @labcraft/lab/ui/components/lab-shell
 *
 * Renders {@link LabAppBar} (the lab-specific 56px app bar) at the top,
 * the routed body in the middle, and {@link Footer} (the lab-local
 * copyright line) at the bottom — except in the chapter reader, which is
 * full-bleed (design §6) and drops the Footer. Also owns the cross-page
 * `SessionExpiredWatcher` effect — moved off the router root because
 * the watcher only needs to run inside the chromed surface, never on
 * the standalone authorize/verify routes (those are now nested under
 * this shell too, but the watcher's `prevHadLearner` ref handles the
 * bootstrap-anonymous case so this is incidentally safe).
 *
 * The shell is a flex column that fills the viewport (`min-height:
 * 100vh`); the routed body is `flex: 1` so the chapter page's
 * split-pane fills the remaining height and the Footer always sits
 * below it.
 */

import { useEffect, useLayoutEffect, useRef, type JSX } from 'react'
import { Outlet, useLocation, useNavigate } from 'react-router'
import { useAnalytics } from '@labcraft/ui/hooks'
import { useConfig } from '../hooks/use_config.ts'
import { useMe } from '../hooks/use_me.ts'
import { useLearnerAnalytics } from '../hooks/use_learner_analytics.ts'
import { Footer } from './site_chrome.tsx'
import { LabAppBar } from './lab_app_bar.tsx'
import { ReaderChromeProvider } from '../lib/reader_chrome.tsx'
import styles from './lab_shell.module.css'

/** Root layout component. Mounted by every route via React Router. */
export function LabShell(): JSX.Element {
  // Inject the Umami tracker once when analytics is configured via
  // /api/config (operator-set in the admin lab settings panel). No-op
  // while unset — the learner surface ships analytics-dark by default.
  // The hook is the shared, dependency-agnostic copy from
  // `@labcraft/ui`; lab resolves the config and passes it in. Lab's
  // website ID is distinct from www's, so learner pageviews report to a
  // separate Umami website.
  //
  // `autoTrack: false` — lab drives pageviews itself (below) so it can
  // `identify()` a known learner before their first pageview. With
  // auto-track on, the tracker fires that pageview during its own script
  // execution, before identify can run, opening an anonymous "shadow"
  // session for every signed-in learner (one person shows as 2 visitors).
  const config = useConfig()
  useAnalytics(config.data?.analytics ?? null, { autoTrack: false })

  // Drive Umami identify + pageviews for the learner surface, in order:
  // wait for `/me` to settle, `identify()` the learner (once per session
  // on the real `learners.id` UUID — umbrella #1213, Phase 2), then send
  // the pageview, and re-send on each route change. Ordering identify
  // before the pageview is what prevents the anonymous shadow session.
  // Idempotent across re-renders / StrictMode; no-op while analytics-dark.
  useLearnerAnalytics()

  // Measure the rendered app-bar height and publish it as `--lab-nav-h`
  // on the shell root. The chapter page consumes this to bound its
  // viewport-tall split-pane (`height: calc(100dvh - var(--lab-nav-h))`).
  // The bar is a fixed 56px today, but measuring (rather than hard-
  // coding) keeps the split sizing correct if the bar's height ever
  // changes responsively.
  // The reader is full-bleed (design §6) — no Footer. Match the reader
  // route (`/courses/:courseId/:partId/:chapterId`, 4 path segments
  // under `/courses/`) so the Footer is suppressed only there; every
  // other chromed screen keeps it.
  const location = useLocation()
  const inReader = /^\/courses\/[^/]+\/[^/]+\/[^/]+\/?$/.test(location.pathname)

  const shellRef = useRef<HTMLDivElement | null>(null)
  const navRef = useRef<HTMLDivElement | null>(null)
  useLayoutEffect(() => {
    const shell = shellRef.current
    const nav = navRef.current
    if (!shell || !nav) return
    const publish = (): void => {
      shell.style.setProperty('--lab-nav-h', `${nav.offsetHeight}px`)
    }
    publish()
    const ro = new ResizeObserver(publish)
    ro.observe(nav)
    return () => ro.disconnect()
  }, [])

  return (
    <ReaderChromeProvider>
      <div className={styles.shell} ref={shellRef}>
        <div ref={navRef}>
          <LabAppBar />
        </div>
        <SessionExpiredWatcher />
        <Outlet />
        {!inReader && <Footer />}
      </div>
    </ReaderChromeProvider>
  )
}

////////////////////////////////////////////////////////////////////////////////
// Session expiry watcher
////////////////////////////////////////////////////////////////////////////////

/**
 * Top-level effect that translates a `qkMe` learner going non-null →
 * null (the 401 callback flipped it; see `main.tsx`'s
 * `http.on('unauthorized', ...)`) into a redirect to
 * `/authorize?expired=1&next=…`.
 *
 * Boot-time anonymous (the very first `/me` settling as
 * `{ learner: null }`) must NOT trigger — that's the normal
 * unauthenticated answer, not a session expiry. We distinguish via
 * a `prevHadLearner` ref that flips to `true` the first time we see
 * a non-null learner; the redirect only fires on `true → null`.
 *
 * Explicit signout is also `non-null → null` but is handled by the
 * caller's `mutateAsync().finally(navigate('/authorize'))` chain
 * before the watcher's effect even runs, so the watcher's
 * `?expired=1` redirect tends not to fire there. In the (rare)
 * race where the cache flip lands first, the navigate would
 * still take the learner to `/authorize` with the expired banner —
 * a no-op visual diff from the signed-out flow, not a regression.
 */
function SessionExpiredWatcher(): null {
  const me = useMe()
  const navigate = useNavigate()
  const prevHadLearner = useRef(false)

  useEffect(() => {
    if (me.isPending) return
    const hasLearner = me.data?.learner != null
    if (hasLearner) {
      prevHadLearner.current = true
      return
    }
    // hasLearner === false. Only redirect if we previously had one.
    if (!prevHadLearner.current) return
    prevHadLearner.current = false
    const here = window.location.pathname + window.location.search
    const next = encodeURIComponent(here)
    navigate(`/authorize?expired=1&next=${next}`, { replace: true })
  }, [me.isPending, me.data, navigate])

  return null
}
